
import { FC, useCallback, useEffect, useState } from 'react';
import { Flex } from '@chakra-ui/react';

import { Form } from '@app/components/common/form/form';
import { useAlert, useApi, useEntityState, useGuide } from '@app/hooks';
import { AutocompleteField, TextField, SelectField, FieldView, Txt, SwitchField } from '@app/components/common';
import { FieldComponentProps } from '@app/components/common/types';
import { Employee, Level, StructureNode } from '@app/api/models/structure';
import { usePaginatedAutocomplete } from '@app/hooks/use-autocomplete';

type OrgStructureFormProps = {
  parent?: StructureNode;
  initial?: StructureNode;
  levels: Level[];
  onSubmit?(instance: StructureNode): Promise<void>;
  onCancel?(): void;
};


export const OrgStructureForm = ({
  parent,
  initial,
  levels,
  onSubmit,
  onCancel,
}: OrgStructureFormProps) => {
  const { info, error } = useAlert();
  const api = useApi();
  const [instance, onChange,] = useEntityState<StructureNode>(initial || {
    id: null,
    boss_id: parent ? Number(parent.id) : null,
    territory: '',
    position: null,
    route_number: null,
    employee: null,
    level: getSubLevel(parent?.level, levels),
    is_active: true,
  });

  const [availableLevels, setAvailableLevels] = useState<Level[]>([]);

  const getAvailableLevels = useCallback(async () => {
    if (availableLevels.length) {return availableLevels;}

    const parentLevel = levels.find(({ id }) => id === parent?.level?.id);
    if (!initial) { 
      return levels.filter(({ value }) => value < parentLevel.value).map(({ id, name }) => ({ id, name }));
    }

    const levelsMap = new Map<number, Level>();
    levels.forEach((level) => levelsMap.set(Number(level.id), level));

    const { data } = await api.structure.getNodes({
      filter: { 
        boss_id: initial.id.toString(),
      },
      sort: '+id',
      limit: 10000,
      offset: 0,
    });

    const maxChildrenLevel = data.data.map(({ level }) => level).reduce<Level | null>((max, level) => {
      if (!max) {return levelsMap.get(Number(level.id));}
      if (levelsMap.get(Number(level.id)).value > max.value) {return levelsMap.get(Number(level.id));}
      return max;
    }, null);

    const _availableLevels = levels.filter(({ value }) => value < (parentLevel?.value || Infinity) && value > (maxChildrenLevel?.value || -Infinity));  

    setAvailableLevels(_availableLevels);
    return _availableLevels;

  }, [availableLevels, levels, initial, api.structure, parent?.level.id]);

  const getAutocomplete = usePaginatedAutocomplete();

  const onFetchEmployees = useCallback(async (name: string, query: Record<string, string>) => {
    const search = query.search || '';
    
    const response = await api.structure.getEmployees({
      filter: {
        name: search,
        is_active: 'true',
      },
      sort: '+id',
      limit: 10,
      offset: 0,
    });
    if (response.code === 200) {
      return response.data.data;
    }
    return Promise.resolve([]);
  }, []);
  
  const onSubmitForm = async (instance: StructureNode) => {
    let response;
    if (!initial) {
      response = await api.structure.createNode({
        level_id: Number(instance.level.id),  
        boss_id: instance.boss_id,
        employee_id: Number(instance.employee?.id),
        position_id: Number(instance.position.id),
        route_number_id: Number(instance.route_number?.id),
        territory: instance.territory,
      });
    } else {
      response = await api.structure.updateNode({
        id: instance.id,
        level_id: Number(instance.level.id),  
        boss_id: instance.boss_id,
        employee_id: Number(instance.employee?.id),
        position_id: Number(instance.position.id),
        route_number_id: Number(instance.route_number?.id),
        territory: instance.territory,
        is_active: instance.is_active,
      });
    }
    if (response.code === 200) {
      info(response.message);
      await onSubmit(instance);
    }
  };

  return <Form
    instance={instance}
    onSubmit={onSubmitForm}
    onCancel={onCancel}
    onChange={onChange}
  >
    {parent && <Form.Spacer span={{ md: 12 }}>
      <FieldView title="Вышестоящий уровень/территория" value={<Flex gap={2} alignItems='center'>
        <Txt color='brand.500'>{parent.level?.name}</Txt>
        <Txt color='gray.500'>{parent.territory}</Txt>
      </Flex>}
      />
    </Form.Spacer>}
    <Form.Item
      label="Уровень"
      field='level'
      required
      component={SelectField}
      componentProps={{
        onFetchOptions: getAvailableLevels,
      }}
      span={{ md: 12 }}
    />
    <Form.Item
      label="Территория"
      field='territory'
      required
      component={TextField}
      span={{ md: 12 }}
    />
    <Form.Item
      field='position'
      label='Должность'
      required
      component={SelectField}
      componentProps={{
        onFetchOptions: useGuide('user_position')
      }}
      span={{ md: 12 }}
    />
    <Form.Item<StructureNode, Employee>
      label="Сотрудник"
      field='employee'
      component={AutocompleteField}
      componentProps={{
        name: 'employee',
        valueProperty: 'name',
        onFetchOptions: onFetchEmployees,
      }}
      span={{ md: 12 }}
    />
    <Form.Item
      label="Номер маршрута"
      field='route_number'
      component={RouteNumberField}
      componentProps={{
        name: 'route_number',
        valueProperty: 'name',
        onFetchOptions: getAutocomplete,
      }}
      span={{ md: 12 }}
    />
    <Form.Item
      label="Статуc aктивности"
      field='is_active'
      component={SwitchField}
      span={{ md: 12 }}
    />
  </Form>;
};

const getSubLevel = (level: Level | null, levels: Level[]) => {
  if (!level) {return levels[0];}
  const index = levels.findIndex(({ id }) => id === level.id);
  return levels[index + 1];
};



const RouteNumberField: FC<FieldComponentProps<EntityBase>> = ({ value, onChange, ...props }) => { 
  return <AutocompleteField 
    id='route_number'  
    value={value}
    onChange={onChange}
    {...props}
  />;
}; 